import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

import { dropKeys, hasKey, importHistoryKey, retainKey } from './cipher';

interface HistorySession {
  owner: string | null;
  /** History is locked for this owner only until a lookup confirms them again: the page may have
   *  slept through a sign-out, or the same account's key is importing again. A confirmation sends
   *  the page back to the conversation that was open (see useReturnThread). */
  lockedOwner: string | null;
  /** Undefined until a lookup on this page has answered. */
  signedIn: boolean | undefined;
  /** Website auth only: signed in through the in-chat sign-in, standing in for the site's login. */
  inChat: boolean;
  /** For the welcome greeting. */
  firstName?: string;
}

export const useHistorySession = create<HistorySession>(() => ({
  owner: null,
  lockedOwner: null,
  signedIn: undefined,
  inChat: false,
  firstName: undefined,
}));
export type SessionNote = {
  signedIn?: boolean;
  scope?: string;
  historyKey?: string;
  inChat?: boolean;
  firstName?: string;
} | null;
let generation = 0;

/** Moves on every session answer or lock, same account included, so a request can tell whether
 *  the session was looked at again while it was out. */
export function sessionGeneration(): number {
  return generation;
}

/** A newer answer invalidates pending imports before they can publish a key. */
export async function noteSession(body: SessionNote): Promise<void> {
  const current = ++generation;
  const signedIn = Boolean(body?.signedIn);
  const inChat = Boolean(body?.inChat);
  const firstName = signedIn ? body?.firstName : undefined;
  function publishOwner({
    owner,
    lockedOwner = null,
  }: Pick<HistorySession, 'owner'> & {
    lockedOwner?: string | null;
  }) {
    publish({ owner, lockedOwner, signedIn, inChat, firstName });
  }
  const next =
    body?.signedIn && body.scope && body.historyKey
      ? { owner: body.scope, historyKey: body.historyKey }
      : null;
  if (!next) {
    dropKeys();
    publishOwner({ owner: null });
    return;
  }
  const { owner, historyKey } = next;
  const held = useHistorySession.getState();
  if (owner === held.owner && hasKey(owner)) {
    publishOwner({ owner });
    return;
  }
  // Hide the old account while the replacement key is importing. The same account is only
  // locked meanwhile, not lost.
  const sameAccount = owner === held.owner || owner === held.lockedOwner;
  dropKeys();
  publishOwner({ owner: null, lockedOwner: sameAccount ? owner : null });
  try {
    const key = await importHistoryKey(historyKey);
    if (current !== generation) return;
    retainKey({ owner, key });
    publishOwner({ owner });
  } catch (error) {
    if (current === generation) {
      dropKeys();
      publishOwner({ owner: null });
    }
    throw error;
  }
}

/** Locks history without changing who is signed in: for a page that may have slept through a
 *  sign-out, until a fresh lookup says whether the key still belongs here. */
export function suspendKey(): void {
  generation++;
  dropKeys();
  const { owner, lockedOwner, signedIn, inChat, firstName } = useHistorySession.getState();
  publish({ owner: null, lockedOwner: owner ?? lockedOwner, signedIn, inChat, firstName });
}

function publish(next: HistorySession): void {
  if (!shallow(useHistorySession.getState(), next)) useHistorySession.setState(next);
}
